import { FEAT, formFx, SKILL_FX } from '@somnhold/content';
import { cellAtWorld, cellHex, heroWalkable } from '../grid.ts';
import { DIRS, hexToWorld } from '../hex.ts';
import { dist2, idiv, imax, imin, mulBp, withinRange } from '../math/fixed.ts';
import type { BattleState, Bot, Enemy, Hero, Structure, UqDue, Zone } from '../types.ts';
import { damageEnemy, enemiesNear, ev, healHero, isFlying, targetable, wardHero } from './combat.ts';
import { stepToward } from './enemies.ts';
import { featPieceTest, featUse } from './feats.ts';
import { formFinal } from './forms.ts';
import { raiseShield } from './kits.ts';
import { bigOne, due, heroById, pieceStun } from './panoply.ts';
import { shove } from './skills.ts';
import { firesAsTower, slowFor } from './towers.ts';
import { fx, hasUq } from './uniques.ts';

/**
 * The artificer's panoply in a fight (docs/PANOPLIA.md §10), as battle/panoply.ts does the warrior's: each piece changes
 * its skill where the skill's program, zone or summon calls it (a zone or a summon of a piece carries its id in `uq`),
 * what comes later waits in the pieces' queue, and what the feats ask is counted here, a line each.
 */

const heroOfPid = (s: BattleState, pid: string): Hero | undefined => s.heroes.find((h) => h.pid === pid);
/** A zone's damage type, as damage options (physical adds nothing), as the skills' own zones do. */
const zt = (z: Zone, own: 'physical' | 'lightning' | 'fire'): { type?: Zone['dt'] } => {
  const t = z.dt ?? own;
  return t !== 'physical' ? { type: t } : {};
};
const hits = (s: BattleState, x: number, y: number, r: number): Enemy[] => enemiesNear(s, x, y, r, (o) => targetable(s, o));

/* ---------- Pico del minero (Mina de clavos) ---------- */

/** A mine's burst, as the skill's own (skills.ts): its blow and its slow on all within reach. Returns how many it felled. */
const mineBlast = (s: BattleState, z: Zone, dmg: number): number => {
  let felled = 0;
  for (const e of hits(s, z.x, z.y, z.radius)) {
    damageEnemy(s, e, dmg, { pid: z.pid, ...zt(z, 'physical') });
    if (e.hp <= 0) felled++;
    const sl = slowFor(e, z.slowBp);
    if (e.slowBp < sl) e.slowBp = sl;
    if (e.slowTicks < (z.stun ?? 0)) e.slowTicks = z.stun ?? 0;
  }
  ev(s, { t: 'boom', x: z.x, y: z.y, r: z.radius, kind: 'mineBurst' });
  return felled;
};

/** A mine of the pick set off by a nightmare: it bursts as always, and the owner's others near it follow next tick. */
export const minerBurst = (s: BattleState, z: Zone): void => {
  const felled = mineBlast(s, z, z.damage);
  z.ticks = 0;
  const h = heroOfPid(s, z.pid);
  if (!h) return;
  h.mineRun = [1, felled, 0];
  due(s, { at: s.tick + 1, k: 'mineChain', hid: h.id, x: z.x, y: z.y, n: [1] });
};

/** Reguero: the owner's mines within reach of the one that burst go off too (they leave the ground at once). */
const mineChain = (s: BattleState, h: Hero, d: UqDue): void => {
  const f = fx('minerPick', h);
  const gen = d.n?.[0] ?? 1;
  const set: Zone[] = [];
  for (const z of s.zones) if (z.kind === 'mine' && z.uq === 'minerPick' && z.pid === h.pid && z.ticks > 0 && withinRange(z.x, z.y, d.x, d.y, f.range as number)) set.push(z);
  if (!set.length) return;
  s.zones = s.zones.filter((z) => !set.includes(z));
  const run = (h.mineRun ??= [1, 0, 0]);
  for (const z of set) {
    ev(s, { t: 'beam', x: d.x, y: d.y, tx: z.x, ty: z.y, kind: 'mineTrail' });
    run[1] = (run[1] ?? 0) + mineBlast(s, z, mulBp(z.damage, f.damageBp as number));
    run[0] = (run[0] ?? 0) + 1;
    if (gen < (f.gens as number)) due(s, { at: s.tick + 1, k: 'mineChain', hid: h.id, x: z.x, y: z.y, n: [gen + 1] });
  }
  featUse(s, h, 'minerPick', set.length);
  if (!run[2] && (run[0] ?? 0) >= FEAT.need.mineRun && (run[1] ?? 0) >= FEAT.need.mineKills) {
    run[2] = 1;
    featPieceTest(s, h, 'minerPick');
  }
};

/* ---------- Atizador del fogonero (Lanzallamas) ---------- */

/** Fuelle: the jet pushes what it burns; with its final form, what it set alight bursts when it dies burning. */
export const bellows = (s: BattleState, h: Hero, burnt: Enemy[], dmg: number): void => {
  if (!hasUq(h, 'stokerPoker')) return;
  const push = fx('stokerPoker', h).push as number;
  const fin = formFinal(h, 'stokerPoker') ? formFx('stokerPoker', 'final') : null;
  let moved = 0;
  for (const o of burnt) {
    if (o.hp <= 0) continue;
    const x0 = o.x;
    const y0 = o.y;
    shove(s, o, h.x, h.y, push);
    if (o.x !== x0 || o.y !== y0) moved++;
    if (fin) {
      o.emberBy = h.id;
      o.emberDmg = mulBp(dmg, fin.damageBp as number);
    }
  }
  featUse(s, h, 'stokerPoker', moved);
  if (moved >= FEAT.need.bellows) featPieceTest(s, h, 'stokerPoker');
};

const emberBurst = (s: BattleState, h: Hero, d: UqDue): void => {
  const r = formFx('stokerPoker', 'final').radius as number;
  // No chain: what this burst fells doesn't burst in turn.
  const near = hits(s, d.x, d.y, r);
  for (const o of near) o.emberBy = undefined;
  for (const o of near) damageEnemy(s, o, d.n?.[0] ?? 0, { pid: h.pid, type: 'fire' });
  ev(s, { t: 'boom', x: d.x, y: d.y, r, kind: 'emberBurst' });
};

/* ---------- Escuadra del maestro de obras (Llave de ajuste) ---------- */

/** What the wrench mended (`got` of it, from below `before`): it takes less a while and, a tower, fires faster; Remache, it holds. */
export const squareTuned = (s: BattleState, h: Hero, st: Structure, got: number, before: number): void => {
  if (!hasUq(h, 'buildersSquare')) return;
  const f = fx('buildersSquare', h);
  st.tunedUntil = s.tick + (f.ticks as number);
  st.tunedBp = f.takenBp as number;
  if (firesAsTower(st)) {
    if (st.buffTicks < (f.ticks as number)) st.buffTicks = f.ticks as number;
    if (st.buffBp < (f.fireBp as number)) st.buffBp = f.fireBp as number;
  }
  if (formFinal(h, 'buildersSquare')) st.holdUntil = s.tick + (formFx('buildersSquare', 'final').ticks as number);
  featUse(s, h, 'buildersSquare', got);
  if (before * 10000 < st.maxHp * FEAT.need.squareLowBp) due(s, { at: s.tick + FEAT.watch.square, k: 'squareCheck', hid: h.id, x: 0, y: 0, n: [st.id] });
};

/** With nothing hurt in reach the wrench shields the nearest ally (the artificer itself, alone). */
export const squareShield = (s: BattleState, h: Hero): boolean => {
  if (!hasUq(h, 'buildersSquare')) return false;
  const f = fx('buildersSquare', h);
  let best: Hero = h;
  let bd = 0x7fffffff;
  for (const o of s.heroes) {
    if (o.id === h.id || o.downTicks > 0 || !withinRange(o.x, o.y, h.x, h.y, f.reach as number)) continue;
    const d = dist2(o.x, o.y, h.x, h.y);
    if (d < bd) {
      bd = d;
      best = o;
    }
  }
  const amount = mulBp(best.stats.maxHp, f.shieldBp as number);
  raiseShield(best, amount, f.ticks as number, false);
  featUse(s, h, 'buildersSquare', amount);
  ev(s, { t: 'boom', x: best.x, y: best.y, r: 600, kind: 'squareShield' });
  return true;
};

/** What a structure takes with the wrench's tuning on it, against `bp` of today's covers (the stronger stays). */
export const tunedBp = (s: BattleState, st: Structure, bp: number): number => ((st.tunedUntil ?? 0) > s.tick && (st.tunedBp ?? 10000) < bp ? (st.tunedBp as number) : bp);

const squareCheck = (s: BattleState, h: Hero, d: UqDue): void => {
  const st = s.structures.find((o) => o.id === d.n?.[0]);
  if (st && !st.destroyed) featPieceTest(s, h, 'buildersSquare');
};

/* ---------- Engranaje del relojero (Dron de reparación) ---------- */

/** Nothing to mend near: the drone goes to the most hurt ally within its reach of its maker and mends it. False: none. */
export const droneHeals = (s: BattleState, b: Bot, o: Hero): boolean => {
  if (b.uq !== 'clockmakerGear') return false;
  let best: Hero | null = null;
  let worst = 10000;
  for (const a of s.heroes) {
    if (a.downTicks > 0 || a.hp >= a.stats.maxHp || !withinRange(a.x, a.y, o.x, o.y, b.radius)) continue;
    const left = idiv(a.hp * 10000, a.stats.maxHp);
    if (left < worst) {
      worst = left;
      best = a;
    }
  }
  if (!best) return false;
  const reach = SKILL_FX.repairDrone.reach;
  if (!withinRange(b.x, b.y, best.x, best.y, reach)) {
    b.anim = 'walk';
    stepToward(b, best.x, best.y, SKILL_FX.repairDrone.step);
    return true;
  }
  if (b.cd > 0) return true;
  const got = healHero(s, best, mulBp(b.damage, fx('clockmakerGear', o).healBp as number), o.pid);
  b.cd = b.every;
  b.anim = 'attack';
  b.animT = 0;
  ev(s, { t: 'boom', x: best.x, y: best.y, r: 400, kind: 'droneHeal' });
  featUse(s, o, 'clockmakerGear', got);
  droneFlight(s, b, o, got);
  return true;
};

/** What a drone of the gear mended or healed this flight: once past the test's, it counts (once a flight). */
export const droneFlight = (s: BattleState, b: Bot, o: Hero, got: number): void => {
  if (b.uq !== 'clockmakerGear' || (b.got ?? 0) < 0) return;
  b.got = (b.got ?? 0) + got;
  if (b.got >= FEAT.need.droneFlight) {
    b.got = -1;
    featPieceTest(s, o, 'clockmakerGear');
  }
};

/** Segundo dron: one more beside the first (as the pack glyph lays it). */
export const secondDrone = (s: BattleState, h: Hero): void => {
  const bots = s.bots ?? [];
  let last: Bot | undefined;
  for (const b of bots) if (b.kind === 'drone' && b.owner === h.id) last = b;
  if (last) bots.push({ ...last, id: s.nextId++, x: last.x + 400 });
};

/* ---------- Remache del cerrajero (Barricada de chapa) ---------- */

/** Ruedas: every so often the barricade rolls a hex toward the nearest nightmare and pushes what is in front. */
const barricadeRolls = (s: BattleState, z: Zone, h: Hero): void => {
  const f = fx('locksmithRivet', h);
  if (z.ticks <= 0 || z.ticks % (f.every as number) !== 0 || !z.cells.length) return;
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of enemiesNear(s, z.x, z.y, f.hunt as number, (o) => targetable(s, o) && !isFlying(o))) {
    const d = dist2(e.x, e.y, z.x, z.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  if (!best || bd <= 1000 * 1000) return;
  // The hex step that brings it nearest.
  const R = s.map.radius;
  let dir = DIRS[0] as { q: number; r: number };
  let dd = 0x7fffffff;
  const c0 = cellHex(R, z.cells[0] as number);
  const w0 = hexToWorld(c0.q, c0.r);
  for (const k of DIRS) {
    const w = hexToWorld(c0.q + k.q, c0.r + k.r);
    const d = dist2(w.x - w0.x + z.x, w.y - w0.y + z.y, best.x, best.y);
    if (d < dd) {
      dd = d;
      dir = k;
    }
  }
  const moved: number[] = [];
  for (const id of z.cells) {
    const c = cellHex(R, id);
    const w = hexToWorld(c.q + dir.q, c.r + dir.r);
    const to = cellAtWorld(s, w.x, w.y);
    if (!heroWalkable(s, to)) return;
    moved.push(to);
  }
  const step = hexToWorld(dir.q, dir.r);
  z.cells = moved;
  z.x += step.x;
  z.y += step.y;
  let pushed = 0;
  for (const e of s.enemies) {
    if (e.hp <= 0 || isFlying(e) || !targetable(s, e) || !moved.includes(cellAtWorld(s, e.x, e.y))) continue;
    const x0 = e.x;
    const y0 = e.y;
    shove(s, e, e.x - step.x, e.y - step.y, f.push as number);
    damageEnemy(s, e, f.damage as number, { pid: z.pid });
    if (e.x !== x0 || e.y !== y0) pushed++;
  }
  ev(s, { t: 'boom', x: z.x, y: z.y, r: 900, kind: 'barricadeRoll' });
  featUse(s, h, 'locksmithRivet', pushed);
  if (pushed >= FEAT.need.rolled) featPieceTest(s, h, 'locksmithRivet');
};

/** Chapa que revienta: at its end each plate bursts, stunning around (a piece's stun). */
const plateBurst = (s: BattleState, z: Zone, h: Hero): void => {
  if (!formFinal(h, 'locksmithRivet')) return;
  const f = formFx('locksmithRivet', 'final');
  const R = s.map.radius;
  const seen = new Set<number>();
  for (const id of z.cells) {
    const c = cellHex(R, id);
    const w = hexToWorld(c.q, c.r);
    for (const e of hits(s, w.x, w.y, f.radius as number)) {
      if (seen.has(e.id)) continue;
      seen.add(e.id);
      damageEnemy(s, e, f.damage as number, { pid: z.pid });
      const st = pieceStun(e, f.stun as number);
      if (st > e.stun) e.stun = st;
    }
    ev(s, { t: 'boom', x: w.x, y: w.y, r: f.radius as number, kind: 'plateBurst' });
  }
};

/* ---------- Cobre del pararrayero (Bobina) ---------- */

/** Última chispa: as it ends, the coil throws a bolt at each nightmare within reach. */
const lastSpark = (s: BattleState, z: Zone, h: Hero): void => {
  const f = fx('rodmanCopper', h);
  const dmg = mulBp(z.damage, f.damageBp as number);
  let n = 0;
  let felled = 0;
  for (const e of hits(s, z.x, z.y, f.radius as number)) {
    damageEnemy(s, e, dmg, { pid: z.pid, ...zt(z, 'lightning') });
    if (e.hp <= 0) felled++;
    // Six arcs drawn at most (docs/PANOPLIA.md §9.5).
    if (n++ < 6) ev(s, { t: 'beam', x: z.x, y: z.y, tx: e.x, ty: e.y, kind: 'sparkArc' });
  }
  ev(s, { t: 'boom', x: z.x, y: z.y, r: f.radius as number, kind: 'lastSpark' });
  featUse(s, h, 'rodmanCopper', felled);
  if (n >= FEAT.need.spark) featPieceTest(s, h, 'rodmanCopper');
};

/* ---------- Peto del artillero (Mortero) ---------- */

/** A shell of the plate's mortar is about to land at (x, y): it leaves a crater, and who it reaches is counted. */
export const mortarShell = (s: BattleState, z: Zone, x: number, y: number): void => {
  const h = heroOfPid(s, z.pid);
  if (!h) return;
  const f = fx('gunnerPlate', h);
  // The cap: the oldest crater goes.
  let n = 0;
  let old: Zone | null = null;
  for (const o of s.zones) {
    if (o.kind !== 'quake' || o.uq !== 'gunnerPlate' || o.pid !== z.pid || o.ticks <= 0) continue;
    n++;
    if (!old) old = o;
  }
  if (old && n >= (f.cap as number)) old.ticks = 0;
  s.zones.push({ id: s.nextId++, kind: 'quake', x, y, radius: mulBp(z.radius, f.radiusBp as number), cells: [], ticks: f.ticks as number, every: 12, damage: 0, slowBp: f.slowBp as number, pid: z.pid, uq: 'gunnerPlate', hit: [] });
  // Who the whole barrage reached (the test, once a cast).
  const seen = (z.hit ??= []);
  for (const e of hits(s, x, y, z.radius)) if (!seen.includes(e.id)) seen.push(e.id);
  if (!z.kills && seen.length >= FEAT.need.mortar) {
    z.kills = 1;
    featPieceTest(s, h, 'gunnerPlate');
  }
};

/** A crater: what walks into it is counted once (the skill's own ground slows it, skills.ts). */
const craterTick = (s: BattleState, z: Zone, h: Hero): void => {
  if (s.tick % 3 !== 0) return;
  const seen = (z.hit ??= []);
  let n = 0;
  for (const e of enemiesNear(s, z.x, z.y, z.radius, (o) => !isFlying(o) && targetable(s, o))) {
    if (seen.includes(e.id)) continue;
    seen.push(e.id);
    n++;
  }
  featUse(s, h, 'gunnerPlate', n);
};

/** Segunda andanada: as the barrage ends, it falls again at a share (once). */
const secondVolley = (z: Zone, h: Hero): void => {
  if (z.hop || !formFinal(h, 'gunnerPlate')) return;
  z.hop = true;
  z.damage = mulBp(z.damage, formFx('gunnerPlate', 'final').damageBp as number);
  z.ticks = imax(1, (z.pulses ?? 0) * z.every);
  z.pulses = 0;
};

/* ---------- Mandil del albañil (Fortificar) ---------- */

/** Piedra y gente: the allies within take less (the damage-taken family); the allies inside are counted for the test. */
const apronTick = (s: BattleState, z: Zone, h: Hero): void => {
  if (z.ticks > 0 && s.tick % 3 === 0) {
    const bp = fx('masonApron', h).takenBp as number;
    let allies = 0;
    for (const o of s.heroes) {
      if (o.downTicks > 0 || !withinRange(o.x, o.y, z.x, z.y, z.radius)) continue;
      wardHero(o, bp, 4);
      o.apronT = s.tick + 4;
      o.apronBp = bp;
      o.apronBy = h.id;
      if (o.id !== h.id) allies++;
    }
    // As the tests that count allies (docs/UNICAS.md §2.2): at most the others in the fight, never under one.
    const need = imax(1, imin(FEAT.need.apron, s.heroes.length - 1));
    const t = (z.uqn ??= [0, 0]);
    t[0] = allies >= need ? (t[0] ?? 0) + 3 : 0;
    if (!t[1] && (t[0] ?? 0) >= FEAT.need.apronHold) {
      t[1] = 1;
      featPieceTest(s, h, 'masonApron');
    }
  }
  if (z.ticks > 0 || !formFinal(h, 'masonApron')) return;
  // Cal y canto: what was in it, a shield.
  const f = formFx('masonApron', 'final');
  for (const o of s.heroes) if (o.downTicks === 0 && withinRange(o.x, o.y, z.x, z.y, z.radius)) raiseShield(o, mulBp(o.stats.maxHp, f.shieldBp as number), f.ticks as number, false);
  for (const st of s.structures) {
    if (st.destroyed || st.maxHp <= 0) continue;
    const w = hexToWorld(st.q, st.r);
    if (withinRange(w.x, w.y, z.x, z.y, z.radius)) raiseShield(st, mulBp(st.maxHp, f.shieldBp as number), f.ticks as number, false);
  }
  ev(s, { t: 'boom', x: z.x, y: z.y, r: z.radius, kind: 'limeAndStone' });
};

/** A blow on a hero under the apron: what it spared is counted for its owner. */
export const apronHurt = (s: BattleState, h: Hero, dmg: number): void => {
  if (!h.apronT || h.apronT <= s.tick || h.apronBy === undefined) return;
  const o = heroById(s, h.apronBy);
  const bp = h.apronBp ?? 10000;
  if (o && bp > 0) featUse(s, o, 'masonApron', idiv(dmg * (10000 - bp), bp));
};

/* ---------- Coleto del perrero (Perro de relojería) ---------- */

/** Presa: what the hound bites is held a moment (once every few seconds each); a held elite drawn to it with Olfato. */
export const houndBit = (s: BattleState, b: Bot, bitten: Enemy[]): void => {
  if (b.uq !== 'kennelCoat') return;
  const o = heroById(s, b.owner);
  if (!o) return;
  const f = fx('kennelCoat', o);
  const fin = formFinal(o, 'kennelCoat') ? formFx('kennelCoat', 'final') : null;
  let n = 0;
  for (const e of bitten) {
    if (e.hp <= 0 || e.kind === 'dragon' || e.kind === 'boneWall' || bigOne(e) || (e.pinCd ?? 0) > s.tick) continue;
    const t = e.elite ? idiv(f.pin as number, 2) : (f.pin as number);
    e.slowBp = 10000;
    if (e.slowTicks < t) e.slowTicks = t;
    e.pinUntil = s.tick + t;
    e.pinBy = o.id;
    e.pinCd = s.tick + (f.every as number);
    n++;
    ev(s, { t: 'boom', x: e.x, y: e.y, r: 400, kind: 'houndPin' });
    if (fin && e.elite) {
      e.lureBot = b.id;
      e.lureUntil = s.tick + (fin.ticks as number);
    }
  }
  featUse(s, o, 'kennelCoat', n);
};

/** Olfato: the hound goes first for the nearest elite it can hunt. */
export const houndScent = (s: BattleState, b: Bot, best: Enemy | null, ax: number, ay: number, hunt: number): Enemy | null => {
  if (b.uq !== 'kennelCoat') return best;
  const o = heroById(s, b.owner);
  if (!o || !formFinal(o, 'kennelCoat')) return best;
  let elite: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of enemiesNear(s, ax, ay, hunt, (x) => x.elite && !bigOne(x) && targetable(s, x) && !isFlying(x))) {
    const d = dist2(e.x, e.y, b.x, b.y);
    if (d < bd) {
      bd = d;
      elite = e;
    }
  }
  return elite ?? best;
};

/** The hound an elite is drawn to by Olfato, if it still stands. */
export const lureOf = (s: BattleState, e: Enemy): Bot | null => {
  if (!e.lureUntil || e.lureUntil <= s.tick) return null;
  return s.bots?.find((b) => b.id === e.lureBot && b.hp > 0) ?? null;
};

/* ---------- each zone, each death, the queue ---------- */

/** Every tick, for a zone of an artificer's piece (after its own ground did its part): what the piece adds, and its end. */
export const artificerZone = (s: BattleState, z: Zone): void => {
  const h = heroOfPid(s, z.pid);
  if (!h) return;
  if (z.uq === 'locksmithRivet') {
    barricadeRolls(s, z, h);
    if (z.ticks <= 0) plateBurst(s, z, h);
  } else if (z.uq === 'rodmanCopper') {
    if (z.ticks <= 0) lastSpark(s, z, h);
  } else if (z.uq === 'gunnerPlate') {
    if (z.kind === 'quake') craterTick(s, z, h);
    else if (z.ticks <= 0) secondVolley(z, h);
  } else if (z.uq === 'masonApron') apronTick(s, z, h);
};

/** A nightmare died: the stoker's embers burst, the kennel's held elite counts. */
export const artificerDied = (s: BattleState, e: Enemy): void => {
  if (e.emberBy !== undefined && e.dotTicks > 0 && e.dotType === 'fire') {
    const h = heroById(s, e.emberBy);
    if (h && formFinal(h, 'stokerPoker')) due(s, { at: s.tick + 1, k: 'emberBurst', hid: h.id, x: e.x, y: e.y, n: [e.emberDmg ?? 0] });
  }
  if (e.elite && e.pinBy !== undefined && (e.pinUntil ?? 0) > s.tick) {
    const h = heroById(s, e.pinBy);
    if (h) featPieceTest(s, h, 'kennelCoat');
  }
};

/** What the artificer's pieces left for this tick. False: not one of theirs. */
export const artificerDue = (s: BattleState, h: Hero, d: UqDue): boolean => {
  if (d.k === 'mineChain') mineChain(s, h, d);
  else if (d.k === 'emberBurst') emberBurst(s, h, d);
  else if (d.k === 'squareCheck') squareCheck(s, h, d);
  else return false;
  return true;
};
